'use client';

import React from 'react';
import { Ship, Truck, Box, ArrowUpDown, LineChart, BarChart2, Globe, Clock, Shield } from 'lucide-react';
import { usePageView } from '@/lib/hooks/usePageView';
import { CTASection } from '../_PageSections/PageCTA';

const FeatureCard = ({ title, description, icon: Icon }) => {
  return (
    <div className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow">
      <div className="flex items-center gap-4 mb-4">
        <div className="p-2 bg-orange-100 rounded-lg">
          <Icon className="h-6 w-6 text-orange-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
      </div>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const ProductDetailLayout = ({ title, description, features }) => {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-6">
            {title}
          </h1>
          <p className="text-xl text-black max-w-3xl mx-auto">
            {description}
          </p>
        </div>
  
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 mb-16">
          {features.map((feature, index) => (
            <FeatureCard key={index}
{...feature} />
          ))}
        </div>
  
        <CTASection />
      </div>
    );
  };

export function SupplyChainIntel() {
  usePageView();
  
  const features = [
    {
      title: 'Real-Time Tracking',
      description: 'Monitor shipment locations, status updates, and ETAs through an intuitive dashboard',
      icon: Truck
    },
    {
      title: 'Performance Analytics',
      description: 'Track KPIs, identify trends, and optimize supply chain performance with data-driven insights',
      icon: LineChart
    },
    {
      title: 'Document Management',
      description: 'Centralize and manage shipping documents, customs paperwork, and certificates in one place',
      icon: Box
    },
    {
      title: 'Route Optimization',
      description: 'Plan optimal shipping routes considering costs, transit times, and potential disruptions',
      icon: Globe
    },
    {
      title: 'Alert System',
      description: 'Get notified of delays, exceptions, and potential issues before they impact operations',
      icon: Clock
    },
    {
      title: 'Compliance Tracking',
      description: 'Ensure regulatory compliance with automated checks and documentation verification',
      icon: Shield
    }
  ];

  return (
    <ProductDetailLayout 
        title="Supply Chain Intelligence Platform"
        description="Gain complete visibility into your supply chain with real-time tracking, analytics, and intelligent insights."
        features={features}
    />        
  );
}

export function RatesIntel() {
  usePageView();
  
  const features = [
    {
      title: 'Smart Quotation',
      description: 'Generate accurate quotes instantly using real-time market rates and carrier data',
      icon: ArrowUpDown
    },
    {
      title: 'Rate Management',
      description: 'Centralize and manage carrier contracts, surcharges, and special handling fees',
      icon: BarChart2
    },
    {
      title: 'Route Comparison',
      description: 'Compare different routing options, transit times, and total landed costs',
      icon: Ship
    },
    {
      title: 'Historical Analysis',
      description: 'Track rate trends and patterns to optimize shipping costs and negotiations',
      icon: LineChart
    },
    {
      title: 'Automated Updates',
      description: 'Keep rates current with automated carrier rate updates and GRI notifications',
      icon: Clock
    },
    {
      title: 'Cost Optimization',
      description: 'Identify cost-saving opportunities and optimize shipping spend across lanes',
      icon: Box
    }
  ];

  return (
    <ProductDetailLayout 
        title="Rates Intelligence Platform"
        description="Streamline your quoting process and optimize shipping costs with our intelligent rate management system."
        features={features}
    /> 
  );
}

export function MarketIntel() {
  usePageView();
  
  const features = [
    {
      title: 'Tariff Database',
      description: 'Access comprehensive, up-to-date tariff information and HTS code lookup tools',
      icon: Globe
    },
    {
      title: 'Market Analytics',
      description: 'Track market trends, capacity changes, and pricing fluctuations in real-time',
      icon: LineChart
    },
    {
      title: 'Port Intelligence',
      description: 'Monitor port congestion, vessel schedules, and operational status updates',
      icon: Ship
    },
    {
      title: 'Trade Compliance',
      description: 'Stay compliant with import/export regulations and documentation requirements',
      icon: Shield
    },
    {
      title: 'Cost Calculator',
      description: 'Calculate accurate landed costs including duties, taxes, and additional charges',
      icon: BarChart2
    },
    {
      title: 'Market Reports',
      description: 'Access detailed market analysis reports and industry forecasts',
      icon: Box
    }
  ];

  return (
    <ProductDetailLayout 
        title="Market Intelligence Platform"
        description="Make informed decisions with comprehensive market data, analytics, and trade compliance tools."
        features={features}
    /> 
  );
}